import { format } from "date-fns";
import type { IntlShape } from "react-intl";

import type { SelectOption } from "components/v2/Select/types";
import { formatNumberV2 } from "helpers/commonHelpers";
import { CONTAINER_HANDLING_UNIT } from "@RequestFormV2/defaults/packingListDefaults";
import type { GoodCustomsInformation, HandlingUnitHashMap, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import type { DangerousGoodsOption } from "@RequestFormV2/sections/PackingList/utils/dangerousGoodsOptions";
import { isTotalWeightUnit } from "@RequestFormV2/sections/PackingList/utils/packingListCalculations";
import type { TemperatureOption } from "@RequestFormV2/sections/PackingList/utils/temperatureOptions";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

export type ReviewPackingListLabelContext = {
  intl: IntlShape;
  handlingUnitHashMap: HandlingUnitHashMap;
  containerTypeOptions: SelectOption[];
  dangerousGoodsOptions: DangerousGoodsOption[];
  temperatureOptions: TemperatureOption[];
  hazardousEnabled: boolean;
  temperatureEnabled: boolean;
  dangerousEnabled: boolean;
  cratingEnabled: boolean;
};

function formatDimensions(row: PackingListRow): string | null {
  if (row.cbm_scope === "piece" && row.l_w_h_cbm_piece != null) {
    return `${row.l_w_h_cbm_piece} CBM/piece`;
  }
  if (row.cbm_scope === "total" && row.l_w_h_cbm_total != null) {
    return `${row.l_w_h_cbm_total} CBM`;
  }
  const { length, width, height, m_unit } = row;
  if (length == null || width == null || height == null || !m_unit) return null;
  return `${length} x ${width} x ${height}${m_unit}`;
}

function formatWeightSuffix(row: PackingListRow, intl: IntlShape): string {
  if (isTotalWeightUnit(row.w_unit)) {
    const isLbs = row.w_unit?.toLowerCase().includes("lbs");
    return intl.formatMessage({
      id: isLbs ? "request-form.review.packingList.perTotalLbs" : "request-form.review.packingList.perTotalKg",
      defaultMessage: isLbs ? "/total lbs" : "/total kg",
    });
  }
  return intl.formatMessage({
    id: "request-form.review.packingList.perUnit",
    defaultMessage: "/unit",
  });
}

function formatWeightLine(
  row: PackingListRow,
  intl: IntlShape,
): { value: string; suffix: string } | null {
  if (row.weight_piece == null) return null;
  const unit = row.w_unit?.replace(/^total_/i, "") ?? "kg";
  return {
    value: `${row.weight_piece}${unit}`,
    suffix: formatWeightSuffix(row, intl),
  };
}

export function formatUnitCellLabel(
  row: PackingListRow,
  { handlingUnitHashMap, containerTypeOptions }: ReviewPackingListLabelContext,
): string {
  if (isContainerRow(row)) {
    const containerLabel = handlingUnitHashMap[CONTAINER_HANDLING_UNIT]?.label ?? "Container";
    const typeLabel = containerTypeOptions.find((opt) => opt.value === row.container_type)?.label;
    return (typeLabel ?? containerLabel).toUpperCase();
  }

  if (row.handling_unit) {
    const label = handlingUnitHashMap[row.handling_unit.toLowerCase()]?.label ?? row.handling_unit;
    return label.toUpperCase();
  }

  return "-";
}

export function formatUnitCellLines(
  row: PackingListRow,
  context: ReviewPackingListLabelContext,
): { label: string; dimensions: string | null; weight: string | null } {
  return {
    label: formatUnitCellLabel(row, context),
    dimensions: formatDimensions(row),
    weight: formatWeightLine(row, context.intl),
  };
}

export function formatBooleanValue(value: boolean | undefined, intl: IntlShape): string {
  return value
    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
    : intl.formatMessage({ id: "general.no", defaultMessage: "No" });
}

export function formatCratingBy(row: PackingListRow): string {
  if (!row.crating_required_by) return "-";
  const parsed = new Date(row.crating_required_by);
  if (Number.isNaN(parsed.getTime())) return row.crating_required_by;
  return format(parsed, "dd MMM yyyy");
}

export function formatTemperatureControl(
  row: PackingListRow,
  { temperatureOptions }: ReviewPackingListLabelContext,
): string {
  if (row.temperature_controlled_custom_value) {
    return row.temperature_controlled_custom_value;
  }

  if (!row.temperature_control_value) return "-";

  const option = temperatureOptions.find((opt) => opt.value === String(row.temperature_control_value));
  if (!option) return "-";

  if (option.supportingText) {
    return `${option.label} - ${option.supportingText}`;
  }

  return option.label;
}

export function formatDangerousGood(
  row: PackingListRow,
  { dangerousGoodsOptions }: ReviewPackingListLabelContext,
): string {
  if (!row.is_dangerous_good || !row.dangerous_good_value) return "-";

  const option = dangerousGoodsOptions.find((opt) => opt.value === String(row.dangerous_good_value));
  if (!option) return "-";

  const classLabel = option.supportingText ? `${option.label} - ${option.supportingText}` : option.label;
  if (row.un_number) {
    const unPrefix = row.un_number.toUpperCase().startsWith("UN") ? "" : "UN ";
    return `${classLabel} (${unPrefix}${row.un_number})`;
  }

  return classLabel;
}

export function formatCommodityValue(commodity: GoodCustomsInformation): string | null {
  if (commodity.value == null) return null;
  return formatNumberV2(Number(commodity.value), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatAdditionalInformation(row: PackingListRow): string {
  return row.additional_field?.trim() || "-";
}
